import React, { useState, useMemo, useEffect } from "react";
import {
  Input,
  Select,
  Textarea,
  FormField,
  Button,
  StatusBadge,
  FileInput,
  SectionTitle,
} from "../../../SharedInputs.jsx";
import { DollarSign, GripVertical, FileText } from "lucide-react";
import { fileStorageService } from "../../../../services/FileStorageService.js";
import { updateStudentAgentCommissions, getStudentAgentCommissions } from "../../../../services/CommissionsService.js";
import { getAgentByName, addPaidCommissionToAgent } from "../../../../services/AgentService.js";
import { getFirstCourseByName } from "../../../../services/CourseService.js";

const COMMISSION_STATUS = ["Pending", "Paid", "Overdue", "Cancelled"];

export const SectionAgentCommission = ({ onCancel, initialData = {} }) => {
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [expandedCommission, setExpandedCommission] = useState({});
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);

  const COURSES = initialData?.formState?.courses || [];
  const AGENT_ID = initialData?.formState?.agencyId || "A342";
  const STUDENT_ID = initialData?.formState?.studentId || "";
  const STUDENT_RECORD_ID = initialData?.id || "";
  const AGENT_NAME = initialData?.formState?.agentName || "";

  useEffect(() => {
    const fetchCommissions = async () => {
      try {
        const data = await getStudentAgentCommissions(STUDENT_ID);
        const docs = Array.isArray(data) ? data : [data];
        const allCommissions = docs.flatMap((doc) =>
          (doc?.commissions || []).map((c) => ({
            studentId: c.studentId || doc?.formState?.studentId || "",
            agentId: c.agentId || doc?.formState?.agentId || "",
            date: c.date || "",
            courseName: c.courseName || "",
            commissionAmount: parseFloat(c.commissionAmount || 0),
            receiptDate: c.receiptDate || "",
            receiptNumber: c.receiptNumber || "",
            receiptDoc: c.receiptDoc || null,
            notes: c.notes || "",
            status: c.status || "Pending",
          }))
        );

        setCommissions(allCommissions);
      } catch (err) {
        console.error("Failed to load commissions:", err);
      }
    };

    if (STUDENT_RECORD_ID && AGENT_ID) {
      fetchCommissions();
    }
  }, [STUDENT_RECORD_ID, AGENT_ID]);

  // Map course -> list of { commission, flatIndex }
  const commissionsDictWithIndex = useMemo(() => {
    const dict = {};
    COURSES.forEach((course) => {
      dict[course.courseName] = commissions
        .map((c, flatIndex) => ({ commission: c, flatIndex }))
        .filter((c) => c.commission.courseName === course.courseName);
    });
    return dict;
  }, [commissions, COURSES]);

  const handleCommissionChange = (courseName, uiIndex, field, value) => {
    const flatIndex = commissionsDictWithIndex[courseName][uiIndex].flatIndex;
    const updated = [...commissions];
    updated[flatIndex] = { ...updated[flatIndex], [field]: value };
    setCommissions(updated);
  };

  const addCommission = async (courseName) => {
    const newCommission = {
      studentId: STUDENT_ID,
      agentId: AGENT_ID,
      studentRecordId: STUDENT_RECORD_ID,
      date: "",
      courseName,
      commissionAmount: 0,
      receiptDate: "",
      receiptNumber: "",
      receiptDoc: null,
      notes: "",
      status: "Pending",
    };

    setCommissions([...commissions, newCommission]);
    setExpandedCourse(courseName);
    setExpandedCommission({
      ...expandedCommission,
      [`${courseName}-${commissions.length}`]: true,
    });
  };

  const toggleCommission = (courseName, idx) => {
    const key = `${courseName}-${idx}`;
    setExpandedCommission({
      ...expandedCommission,
      [key]: !expandedCommission[key],
    });
  };

  const removeCommission = async (courseName, uiIndex) => {
    const flatIndex = commissionsDictWithIndex[courseName][uiIndex].flatIndex;
    const updated = [...commissions];
    updated.splice(flatIndex, 1);
    setCommissions(updated);

    if (STUDENT_ID && AGENT_ID) {
      await updateStudentAgentCommissions({
        studentId: STUDENT_ID,
        agentId: AGENT_ID,
        commissions: updated,
      });
    }
  };

  const saveCommission = async (courseName, uiIndex) => {
    setLoading(true);
    try {
      // Fetch course and agent data
      const courseData = await getFirstCourseByName(courseName);
      const agentData = await getAgentByName(AGENT_NAME);

      if (!courseData || !agentData) {
        alert("Failed to fetch course or agent data.");
        return;
      }

      const flatIndex = commissionsDictWithIndex[courseName][uiIndex].flatIndex;
      const updated = [...commissions];
      let commission = { ...updated[flatIndex] };

      // Set IDs from fetched data
      commission.agentId = agentData.id || commission.agentId || "";
      commission.courseId = courseData.id || commission.courseId || "";
      commission.courseName = courseData.name || commission.courseName || "";

      // Ensure numeric value
      commission.commissionAmount = parseFloat(commission.commissionAmount) || 0;

      // Handle receipt file upload if it's a File
      if (commission.receiptDoc instanceof File) {
        try {
          const uploadedFiles = await fileStorageService.uploadFile(
            commission.receiptDoc,
            `commission/${commission.agentId}/${STUDENT_RECORD_ID}/${commission.studentId || "temp"}`
          );
          commission.receiptDoc = {
            url: uploadedFiles.url || "",
            path: uploadedFiles.path || "",
            name: commission.receiptDoc.name || "",
          };
        } catch (err) {
          console.error("File upload failed:", err);
          alert("Failed to upload receipt. Commission not saved.");
          return;
        }
      } else if (!commission.receiptDoc) {
        commission.receiptDoc = null;
      }

      // Normalize commission
      const normalizedCommission = {
        studentId: commission.studentId || "",
        agentId: commission.agentId || "",
        studentRecordId: commission.studentRecordId || "",
        courseId: commission.courseId || "",
        courseName: commission.courseName || "",
        date: commission.date || "",
        commissionAmount: commission.commissionAmount || 0,
        receiptDate: commission.receiptDate || "",
        receiptNumber: commission.receiptNumber || "",
        receiptDoc: commission.receiptDoc,
        notes: commission.notes || "",
        status: commission.status || "Pending",
      };

      // Update array and set state
      updated[flatIndex] = normalizedCommission;
      setCommissions(updated);

      // Save to student record
      const savedCommission = await updateStudentAgentCommissions(
        STUDENT_RECORD_ID,
        STUDENT_ID,
        agentData.id,
        updated
      );

      // Also update agent record with student's paid commission
      if (normalizedCommission.status === "Paid") {
        // You need a service function for this, e.g., addPaidCommissionToAgent
        await addPaidCommissionToAgent(agentData.id, savedCommission, STUDENT_RECORD_ID);
      }

      alert("Commission saved successfully!");
    } catch (error) {
      console.error("Failed to save commission:", error);
      alert("Failed to save commission. Please try again.");
    } finally {
      setLoading(false);
    }
  };




  // Overall summary across all courses
  const overallSummary = COURSES.reduce(
    (acc, course) => {
      if (!course?.feesPlan) return acc;

      const totalFees = parseFloat(course.feesPlan.totalFees || 0);
      const commissionRate = parseFloat(course.feesPlan.commissionRate || 0);
      const total = (totalFees * commissionRate) / 100;

      const paid = (commissionsDictWithIndex[course.courseName] || []).reduce(
        (sum, c) => sum + (c.commission.status === "Paid" ? parseFloat(c.commission.commissionAmount || 0) : 0),
        0
      );

      acc.total += total;
      acc.paid += paid;
      return acc;
    },
    { total: 0, paid: 0 }
  );

  overallSummary.remaining = overallSummary.total - overallSummary.paid;


  const getCourseSummary = (course) => {
    if (!course?.feesPlan) return { total: 0, paid: 0, remaining: 0 };

    const totalFees = parseFloat(course.feesPlan.totalFees || 0);
    const commissionRate = parseFloat(course.feesPlan.commissionRate || 0);
    const total = (totalFees * commissionRate) / 100;

    const courseCommissions = commissionsDictWithIndex[course.courseName] || [];
    
    const paid = courseCommissions.reduce(
      (sum, item) => sum + (item.commission.status === "Paid" ? parseFloat(item.commission.commissionAmount || 0) : 0),
      0
    );

    const remaining = total - paid;
    return { total, paid, remaining };
  };



  return (
    <div>
      <SectionTitle icon={DollarSign}>Agent Commission Tracking</SectionTitle>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 12,
          background: "#f9fafb",
          padding: 16,
          borderRadius: 8,
          marginBottom: 16,
        }}
      >
        <div><strong>Agent:</strong> {AGENT_NAME || "-"}</div>
        <div><strong>Total Commission:</strong> ${overallSummary.total.toFixed(2)}</div>
        <div><strong>Paid:</strong> ${overallSummary.paid.toFixed(2)}</div>
        <div><strong>Remaining:</strong> ${overallSummary.remaining.toFixed(2)}</div>
        <div>
          <strong>Next Installment Due:</strong>{" "}
          {COURSES.flatMap(c => c.feesPlan?.installments || [])
            .filter(inst => !inst.commissionPaid && inst.dueDate)
            .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0]?.dueDate || "-"}
        </div>
      </div>

      {COURSES.map((course) => {
        const courseComms = commissionsDictWithIndex[course.courseName] || [];
        const summary = getCourseSummary(course);
        const isExpanded = expandedCourse === course.courseName;

        return (
          <div
            key={course.courseName}
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              marginBottom: 12,
              overflow: "hidden",
            }}
          >
            {loading && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(255, 255, 255, 0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  borderRadius: "12px"
                }}
              >
                <div className="spinner" style={{ textAlign: "center" }}>
                  <div className="loader" style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 12px",
                    
                  }} />
                  
                </div>
                <p style={ {
                  padding: "0.5rem",
                }
                }>Saving, please wait!</p>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: 12,
                cursor: "pointer",
                background: "#f9fafb",
                alignItems: "center",
              }}
              onClick={() =>
                setExpandedCourse(isExpanded ? null : course.courseName)
              }
            >
              <GripVertical style={{ cursor: "grab", opacity: 0.5 }} />
              <div style={{ fontWeight: "bold" }}>{course.courseName}</div>
              <div>Total: ${summary.total.toFixed(2)}</div>
              <div>Paid: ${summary.paid.toFixed(2)}</div>
              <div>Remaining: ${summary.remaining.toFixed(2)}</div>
              <div>{isExpanded ? "▲" : "▼"}</div>
            </div>

            {isExpanded && (
              <div style={{ padding: 12, background: "#fff" }}>
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                  <Button size="sm" onClick={() => addCommission(course.courseName)}>
                    + Add Commission
                  </Button>
                </div>

                {courseComms.map((c, idx) => {
                  const commExpanded = expandedCommission[`${course.courseName}-${idx}`];

                  return (
                    <div
                      key={idx}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 6,
                        marginTop: 8,
                        padding: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                          marginBottom: commExpanded ? 8 : 0,
                        }}
                        onClick={() => {
                          const key = `${course.courseName}-${idx}`;
                          setExpandedCommission({
                            ...expandedCommission,
                            [key]: !expandedCommission[key],
                          });
                        }}
                      >
                        <div style={{ fontWeight: "bold" }}>
                          Commission #{idx + 1}
                        </div>
                        <div>Amount: ${c.commission.commissionAmount || 0}</div>
                        <div>
                          <StatusBadge status={c.commission.status || "Pending"} />
                        </div>
                        <div>{commExpanded ? "▲" : "▼"}</div>
                      </div>

                      {commExpanded && (
                        <div>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(3, 1fr)",
                              gap: 12,
                              alignItems: "flex-end",
                              marginBottom: 8,
                            }}
                          >
                            <FormField label="Date">
                              <Input
                                type="date"
                                value={c.commission.date}
                                onChange={(e) =>
                                  handleCommissionChange(course.courseName, idx, "date", e.target.value)
                                }
                              />
                            </FormField>

                            <FormField label="Commission Amount">
                              <Input
                                type="number"
                                value={c.commission.commissionAmount}
                                onChange={(e) =>
                                  handleCommissionChange(course.courseName, idx, "commissionAmount", e.target.value)
                                }
                              />
                            </FormField>

                            <FormField label="Status">
                              <Select
                                value={c.commission.status}
                                onChange={(e) =>
                                  handleCommissionChange(course.courseName, idx, "status", e.target.value)
                                }
                              >
                                {COMMISSION_STATUS.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </Select>
                            </FormField>
                          </div>

                          <SectionTitle icon={FileText}>Receipt / Invoice</SectionTitle>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 8 }}>
                            <FormField label="Receipt Date">
                              <Input
                                type="date"
                                value={c.commission.receiptDate || ""}
                                onChange={(e) =>
                                  handleCommissionChange(course.courseName, idx, "receiptDate", e.target.value)
                                }
                              />
                            </FormField>

                            <FormField label="Receipt / Invoice No">
                              <Input
                                type="text"
                                value={c.commission.receiptNumber || ""}
                                onChange={(e) =>
                                  handleCommissionChange(course.courseName, idx, "receiptNumber", e.target.value)
                                }
                              />
                            </FormField>

                            <FormField label="Receipt Document">
                              <FileInput
                                accept=".pdf,.jpg,.jpeg,.png"
                                value={c.commission.receiptDoc}
                                onChange={(file) =>
                                  handleCommissionChange(course.courseName, idx, "receiptDoc", file)
                                }
                              />
                            </FormField>
                          </div>

                          <FormField label="Notes">
                            <Textarea
                              value={c.commission.notes}
                              onChange={(e) =>
                                handleCommissionChange(course.courseName, idx, "notes", e.target.value)
                              }
                            />
                          </FormField>

                          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                            <Button variant="primary" onClick={() => saveCommission(course.courseName, idx)}>
                              Save
                            </Button>
                            <Button variant="secondary" onClick={() => removeCommission(course.courseName, idx)}>
                              Remove
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
